import {
	createContext,
	useContext,
	useEffect,
	useRef,
	useState,
	cloneElement,
	isValidElement
} from "react"
import anime from "animejs"

const DropdownContext = createContext(null)

export function Dropdown({ children }) {
	const [isOpen, setIsOpen] = useState(false)
	const listRef = useRef(null)
	const containerRef = useRef(null)

	const close = () => {
		const dropdown = listRef.current
		if (!dropdown) return

		anime({
			targets: dropdown,
			scale: 0,
			opacity: 0,
			duration: 300,
			easing: "easeInOutExpo",
			complete: () => {
				dropdown.classList.add("hidden")
				dropdown.removeAttribute("style")
				setIsOpen(false)
			}
		})
	}

	const open = () => {
		document.querySelectorAll("[data-dropdown-list]").forEach(el => {
			if (el !== listRef.current) {
				const target = el
				target.classList.add("hidden")
				target.removeAttribute("style")
			}
		})

		const dropdown = listRef.current
		if (!dropdown) return

		dropdown.classList.remove("hidden")
		dropdown.classList.add("animate__animated", "animate__zoomIn")
		setIsOpen(true)
	}

	const toggle = () => (isOpen ? close() : open())

	useEffect(() => {
		const handleClickOutside = e => {
			if (!containerRef.current?.contains(e.target)) {
				close()
			}
		}
		document.addEventListener("click", handleClickOutside)
		return () => document.removeEventListener("click", handleClickOutside)
	}, [])

	return (
		<DropdownContext.Provider
			value={{ isOpen, toggle, close, listRef, containerRef }}
		>
			<div ref={containerRef} className="relative">
				{children}
			</div>
		</DropdownContext.Provider>
	)
}

function Trigger({ children }) {
	const ctx = useContext(DropdownContext)
	if (!ctx) throw new Error("Dropdown.Trigger must be used within a <Dropdown>")

	if (!isValidElement(children))
		throw new Error("Dropdown.Trigger expects a single React element as child")

	// Clone the child element to attach onClick
	return cloneElement(children, {
		onClick: e => {
			children.props.onClick?.(e)
			ctx.toggle()
		}
	})
}

function List({ children }) {
	const ctx = useContext(DropdownContext)
	if (!ctx) throw new Error("Dropdown.List must be used within a <Dropdown>")

	if (!isValidElement(children))
		throw new Error("Dropdown.List expects a single React element as child")

	// Clone the user’s div and attach the ref and attributes
	return cloneElement(children, {
		ref: ctx.listRef,
		"data-dropdown-list": "",
		...(children.props || {})
	})
}

Dropdown.Trigger = Trigger
Dropdown.List = List

export default Dropdown
